


// Utility functions for intelligent phone number matching

/**
 * Extract phone number from either string, number, or object format
 */
export function extractPhoneValue(phone: string | number | { value: string } | any): string {
    if (typeof phone === 'string') {
        return phone;
    }
    if (typeof phone === 'number') {
        return phone.toString();
    }
    if (phone && typeof phone === 'object' && phone.value) {
        return typeof phone.value === 'number' ? phone.value.toString() : phone.value;
    }
    return '';
}

/**
 * Normalize a phone number to a consistent COMPARISON KEY.
 *
 * NOT the same job as `@privacy-bee/validate`'s `normalizePhone`, and not interchangeable
 * with it. This one defaults an unclassifiable number to `+1` so two spellings
 * of one US number collapse to a single key; the package deliberately attaches
 * no country code, because guessing one stamps a US identity onto a foreign
 * number. A key is allowed an assumption a validator is not.
 *
 * `arePhoneNumbersEqual` and `findMatchingPhone` are built on this, existing
 * stored rows were matched with it, and `phone-matching.spec.ts` pins the `+1`
 * default. Swapping in the package's normalizer changes which records match.
 * Field-level validation is what adopts `@privacy-bee/validate/phone`.
 */
export function normalizePhoneNumber(phone: string | number | { value: string | number } | any): string {
    const phoneStr = extractPhoneValue(phone);
    if (!phoneStr) return '';
    
    // Don't normalize masked phone numbers - return as-is for comparison
    if (/\*{2,}/.test(phoneStr)) {
        return phoneStr;
    }
    
    // Remove all non-numeric characters except +
    let cleaned = phoneStr.replace(/[^\d+]/g, '');
    
    // If it doesn't start with +, assume it's a US number
    if (!cleaned.startsWith('+')) {
        // If it's 10 digits, add +1
        if (cleaned.length === 10) {
            cleaned = '+1' + cleaned;
        }
        // If it's 11 digits and starts with 1, add +
        else if (cleaned.length === 11 && cleaned.startsWith('1')) {
            cleaned = '+' + cleaned;
        }
        // Otherwise add +1 (assuming US)
        else {
            cleaned = '+1' + cleaned;
        }
    }
    
    return cleaned;
}

/**
 * Check if two phone numbers are the same after normalization
 */
export function arePhoneNumbersEqual(phone1: string | number | { value: string | number } | any, phone2: string | number | { value: string | number } | any): boolean {
    return normalizePhoneNumber(phone1) === normalizePhoneNumber(phone2);
}

/**
 * Find matching phone number in a list
 */
export function findMatchingPhone(targetPhone: string | number | { value: string | number } | any, phoneList: (string | number | { value: string | number } | any)[]): string | number | { value: string | number } | any | null {
    const normalizedTarget = normalizePhoneNumber(targetPhone);
    
    for (const phone of phoneList) {
        if (normalizePhoneNumber(phone) === normalizedTarget) {
            return phone;
        }
    }
    
    return null;
}